import React, { useState, useEffect, useMemo } from "react";
import { Box, Modal, Grid, Button } from "@mui/material";
import { FormError } from "./FormError";
import { enqueueSnackbar } from "notistack";
import { RollingTextLoading } from "./RollingTextLoading";
import { verification, submit_verification } from "../api/api";
import { useNavigate } from 'react-router-dom'


export interface verifyModalProps {
    verify: boolean;
    closeModal: Function;
    token: string;
    login: any;
    selectedInterest: string;
    zipcode: string;
    selectedProvider: string;
}

const getMethodHint = (method: string) => {
    if (method === 'email') return 'email';
    if (method === 'phone_call') return 'phone call';
    return 'phone messages';
};

const formatMethodTitle = (key: string) => {
    if (key === 'sms') return 'SMS';
    return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatMethodLabel = (key: string, value: string) => {
    const title = formatMethodTitle(key);
    const detail = value.includes(':')
        ? value.split(':').slice(1).join(':').trim()
        : value.trim();
    return detail ? `${title}: ${detail}` : title;
};

const VerifyModal: React.FC<verifyModalProps> = ({
    verify,
    closeModal,
    token,
    login,
    selectedInterest,
    zipcode,
    selectedProvider
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [code, setCode] = useState("");
    const [isApiSucces, setIsApiSucces] = useState(true);
    const [verifyChoose, setVerifyChoose] = useState<string>('');
    const [onceSelect, setOnceSelect] = useState('');
    const [seconds, setSeconds] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [responseObject, setresponseObject] = useState({});
    const navigate = useNavigate();

    const mfaMethods = useMemo(() => {
        const methods = login?.available_mfa_methods || {};
        return Object.entries(methods).map(([key, value]) => ({
            key,
            label: formatMethodLabel(key, String(value)),
        }));
    }, [login]);

    useEffect(() => {
        if (verify && mfaMethods.length > 0) {
            setVerifyChoose(mfaMethods[0].key);
            setCode('');
            setCodeError(false);
            setOnceSelect('');
            setSeconds(0);
            setIsRunning(false);
        }
    }, [verify, mfaMethods]);

    const chooseChangeHandel = (method: string) => {
        if (method === verifyChoose) return;
        setVerifyChoose(method);
        setCode('');
        setCodeError(false);
        if (onceSelect && onceSelect !== method) {
            enqueueSnackbar(
                `A verification code has been sent to your ${getMethodHint(onceSelect)}. Please check your ${getMethodHint(onceSelect)} for the code.`,
                { variant: "error" }
            );
        }
    };

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        if (isRunning) {
            intervalId = setInterval(() => {
                setSeconds(prevSeconds => prevSeconds - 1);
            }, 1000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isRunning]);

    useEffect(() => {
        if (seconds === 0) {
            setIsRunning(false);
        }
    }, [seconds]);

    const getCode = (method: string) => {
        if (isRunning) return;
        setSeconds(60);
        setIsRunning(true);
        verification(token, method).then((res) => {
            if (res.status === 1) {
                enqueueSnackbar(
                    `The verification code has been sent to your ${getMethodHint(method)}`,
                    { variant: "success" }
                );
                setOnceSelect(method);
            } else if (res.status === -2) {
                enqueueSnackbar(res.msg, { variant: "error" });
            } else {
                setIsApiSucces(false);
                closeHandle(false);
                enqueueSnackbar(
                    "The verification code submission failed. Please try again.",
                    { variant: "error" }
                );
            }
        }).catch(rejected => {
            console.log(rejected);
        });
    };

    const onSubmit = (method: string) => {
        if (submitting) return;
        if (!code) {
            setCodeError(true);
            return;
        }
        setSubmitting(true);
        submit_verification(token, method, code).then((res) => {
            setSubmitting(false);
            if (res.status === 1) {
                console.log(res);
                setresponseObject({...login,...res.response})
                goUserCenter({...login,...res.response});
            } else if (res.status === -1) {
                enqueueSnackbar(
                    "The verification code submission failed. Please try again.",
                    { variant: "error" }
                );
            } else if (res.status === -2) {
                setIsApiSucces(false);
                enqueueSnackbar(
                    "The account data synchronization submission failed. Please close the dialog and try submitting again.",
                    { variant: "error" }
                );
            }
        }).catch(rejected => {
            setSubmitting(false);
            console.log(rejected);
        });
    };
    const goUserCenter = (res?:any) => {

        localStorage.setItem('ez_switch', JSON.stringify(res ? res : responseObject));
        navigate(`/detail?type=${selectedInterest}&zipcode=${zipcode}&provider=${selectedProvider}`)
    };

    const closeHandle = (b?: boolean) => {
        closeModal(b === false ? b : isApiSucces);
        if (b === false || isApiSucces === false) {
            setSeconds(0);
        }
        setIsApiSucces(true);
        setOnceSelect('');
        setCode('');
        setCodeError(false);
    };

    const selectedLabel = mfaMethods.find((m) => m.key === verifyChoose)?.label || '';

    return (
        <>
            <Modal open={verify} onClose={() => { closeHandle() }}>
                <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg rounded-lg p-2 w-full max-w-lg">
                    <Grid container className="flex justify-between mt-1 mb-2">
                        <h2 className="text-xl font-semibold text-gray-700">My Security</h2>
                        <button
                            className="inline-block w-8 h-8 mask bg-[url('../public/images/X.svg')] bg-no-repeat bg-center border-none cursor-pointer"
                            onClick={() => { closeHandle() }}
                        />
                    </Grid>
                    <div className="container mx-auto p-6">
                        <p className="text-slate-500 leading-7 mb-4">
                            If your customer account has two-step verification, you can choose any of the following methods to verify your account.
                        </p>

                        <div className="flex flex-col gap-2 mb-6">
                            {mfaMethods.map((method) => (
                                <button
                                    key={method.key}
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => chooseChangeHandel(method.key)}
                                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
                                        verifyChoose === method.key
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                                    }`}
                                >
                                    <span className="font-medium">{method.label}</span>
                                </button>
                            ))}
                        </div>

                        {verifyChoose && (
                            <div>
                                <div className="mt-2 mb-1 text-sm text-slate-500">{selectedLabel}</div>
                                <div className="w-full max-w-lg flex gap-2">
                                    <input
                                        className="flex-1 bg-transparent border-b border-black py-4 px-2"
                                        id="verificationCode"
                                        name="verificationCode"
                                        placeholder="Enter verification code..."
                                        type="text"
                                        value={code}
                                        onChange={(evt) => {
                                            if (evt.target.value) {
                                                setCodeError(false);
                                            }
                                            setCode(evt.target.value);
                                        }}
                                        disabled={submitting}
                                        maxLength={30}
                                    />
                                    <Button
                                        variant="contained"
                                        className={`${isRunning ? '' : 'bg-blue-600'} text-white rounded w-40 shrink-0`}
                                        disabled={isRunning}
                                        onClick={() => { getCode(verifyChoose) }}
                                    >
                                        {isRunning ? seconds : 'Get code'}
                                    </Button>
                                </div>
                                {codeError && <FormError message="Please enter your verification code" />}
                                <div className="flex justify-center h-10 mt-8 mb-4">
                                    <Button
                                        variant="contained"
                                        onClick={() => { onSubmit(verifyChoose) }}
                                        className={`${submitting ? '' : 'bg-blue-600'} text-white rounded w-full max-w-xs`}
                                        disabled={submitting}
                                    >
                                        submit
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <RollingTextLoading text={[""]} show={submitting} />
                </Box>
            </Modal>
        </>
    );
}

export default VerifyModal;
